/**
 * NavSphere — the Earth as an instrument, not an illustration.
 *
 * FOUNDER, 2026-08-10: "use an interactive 3D sphere/globe as the primary teaching object rather
 * than relying mainly on static coordinate explanations… Do not make this decorative. It should be
 * an instructional instrument… Could the 3D globe become a reusable navigation teaching component
 * that later chapters can call with different overlays?"
 *
 * ── BUILT AS A COMPONENT, NOT AS CHAPTER ONE ──────────────────────────────────────────────────
 * A one-off globe for latitude and longitude would be the same mistake as a second copy of a marking
 * rule: the next chapter would grow its own, and the two would drift. So the sphere knows nothing
 * about chapters. It takes OVERLAYS and MARKS and reports positions back. Chapter 1 is its first
 * caller; the intended others are listed at the bottom of this file so the API is judged against
 * them now rather than discovered to be wrong later.
 *
 * ── WHY CANVAS 2D AND NOT WebGL ───────────────────────────────────────────────────────────────
 * Orthographic projection IS what a globe looks like from outside. It needs no GPU tier, no device
 * measurement, no shader, and it runs on the frugal floor the 2D renderer already established. WebGL
 * would buy shading and cost a dependency plus a class of device that cannot run it — for no
 * pedagogic gain, since what teaches here is the ARC, not the lighting.
 *
 * ── CONFIRMED ON A DEVICE, 2026-08-10 ────────────────────────────────────────────────────────
 * Founder: "checked on my phone, all five labs work."
 *
 * That settles the part no render could: the POINTER HANDLING. Every lab was verified here by
 * screenshot, which proves what is drawn and says nothing about whether a thumb can grab a vessel,
 * whether the Earth spins the way a hand expects, or whether dragging the globe fights the page
 * scroll. Those were reasoned about and could not be measured from this box.
 *
 * Five callers now share this one component — chapters 1, 2, 3, 4 and 9 — and none of them has its
 * own globe. That was the point of building it as a component rather than as chapter one, and it is
 * only worth anything because the shared object behaves correctly in a hand.
 *
 * ── THE GLOBE IS PAPER ────────────────────────────────────────────────────────────────────────
 * Per the paper/chrome ruling: the subject is drawn on this surface, so it does not follow the
 * theme. Ocean stays ocean in either scheme. Theming it would invert the drawing.
 */

import { useRef, useEffect, useState, useCallback } from 'react';

const RAD = Math.PI / 180;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/* ── PROJECTION ───────────────────────────────────────────────────────────────────────────────
   Orthographic, centred on (lat0, lon0). Returns null for a point on the far side, which is the
   whole reason a globe teaches: half the world is genuinely not visible, and a flat map hides that. */
export function project(lat, lon, lat0, lon0, R, cx, cy) {
  const p = lat * RAD, l = (lon - lon0) * RAD, p0 = lat0 * RAD;
  const cosc = Math.sin(p0) * Math.sin(p) + Math.cos(p0) * Math.cos(p) * Math.cos(l);
  if (cosc < 0) return null;                       // behind the limb
  return [cx + R * Math.cos(p) * Math.sin(l),
          cy - R * (Math.cos(p0) * Math.sin(p) - Math.sin(p0) * Math.cos(p) * Math.cos(l))];
}

/**
 * MERCATOR — the same Earth, cut and flattened.
 *
 * y = ln(tan(45° + lat/2)). The logarithm is the whole story: it is what keeps a rhumb line straight
 * and what makes the scale grow as sec(lat), so the two facts a candidate must hold together come
 * from ONE term. The poles are at infinity, so the chart is cut at 85° — which is why a Mercator
 * chart has no pole on it, and is worth seeing rather than being told.
 */
export function mercator(lat, lon, lon0, R, cx, cy, span = 85) {
  const p = clamp(lat, -span, span) * RAD;
  const yMax = Math.log(Math.tan(Math.PI / 4 + span * RAD / 2));
  const dl = (((lon - lon0 + 540) % 360) - 180) * RAD;
  return [cx + (R * dl) / Math.PI, cy - (R * Math.log(Math.tan(Math.PI / 4 + p / 2))) / yMax];
}

/** Screen → lat/lon. Returns null outside the disc, so a drag cannot fall off the Earth. */
export function unproject(x, y, lat0, lon0, R, cx, cy) {
  const dx = (x - cx) / R, dy = (cy - y) / R;
  const rho = Math.hypot(dx, dy);
  if (rho > 1) return null;
  const c = Math.asin(clamp(rho, -1, 1)), p0 = lat0 * RAD;
  const lat = rho === 0 ? lat0
    : Math.asin(clamp(Math.cos(c) * Math.sin(p0) + (dy * Math.sin(c) * Math.cos(p0)) / rho, -1, 1)) / RAD;
  const lon = lon0 + Math.atan2(dx * Math.sin(c),
      rho * Math.cos(c) * Math.cos(p0) - dy * Math.sin(c) * Math.sin(p0)) / RAD;
  return [lat, ((lon + 540) % 360) - 180];
}

/** Degrees → the notation a Second Mate must stay fluent in. 51.4567 → 51° 27.4' N */
export function dm(deg, kind) {
  const hemi = kind === 'lat' ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
  const a = Math.abs(deg), d = Math.floor(a), m = (a - d) * 60;
  const w = kind === 'lat' ? 2 : 3;
  return `${String(d).padStart(w, '0')}° ${m.toFixed(1).padStart(4, '0')}' ${hemi}`;
}

/* A great circle between two positions, sampled. Used by the sailings overlay; declared here so the
   sphere owns its geometry rather than each caller re-deriving it. */
function greatCircle(a, b, n = 96) {
  const [p1, l1] = [a[0] * RAD, a[1] * RAD], [p2, l2] = [b[0] * RAD, b[1] * RAD];
  const d = 2 * Math.asin(Math.sqrt(Math.sin((p2 - p1) / 2) ** 2
          + Math.cos(p1) * Math.cos(p2) * Math.sin((l2 - l1) / 2) ** 2));
  if (!d) return [a, b];
  const out = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n, A = Math.sin((1 - f) * d) / Math.sin(d), B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(p1) * Math.cos(l1) + B * Math.cos(p2) * Math.cos(l2);
    const y = A * Math.cos(p1) * Math.sin(l1) + B * Math.cos(p2) * Math.sin(l2);
    const z = A * Math.sin(p1) + B * Math.sin(p2);
    out.push([Math.atan2(z, Math.hypot(x, y)) / RAD, Math.atan2(y, x) / RAD]);
  }
  return out;
}

/** A rhumb line — constant course — sampled in longitude, so the contrast with the above is visible. */
function rhumb(a, b, n = 96) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    out.push([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f]);
  }
  return out;
}

/**
 * THE NORTH MAGNETIC POLE — a DECLARED approximation, and it moves.
 *
 * Stated with its epoch because it drifts tens of kilometres a year; a position carrying no date is
 * a position that will quietly become wrong. It is used ONLY to show the geometry of why true and
 * magnetic north differ, and why that difference changes with place.
 *
 * IT IS NOT A VARIATION MODEL. Real variation comes from the full geomagnetic field (WMM/IGRF) and
 * is read from the chart. The angle between two bearings computed here is a geometric consequence of
 * two poles, not the value a cadet writes down — and CompassLab says so rather than letting the
 * number pass for one.
 */
export const NMP = { lat: 86.0, lon: 150.0, epoch: 'approx. 2025' };

/** Initial great-circle bearing from a to b: the direction you would steer on leaving. */
export function bearing(aLat, aLon, bLat, bLon) {
  const p1 = aLat * RAD, p2 = bLat * RAD, dl = (bLon - aLon) * RAD;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) / RAD + 360) % 360;
}

const OCEAN = '#0d2233', LAND = '#16344a', GRID = '#2b5570', LIMB = '#5f9ec4';

export default function NavSphere({
  overlays = {}, marks = [], route = null, onMove = null, height = 320, morph = 0,
}) {
  const ref = useRef(null);
  const [rot, setRot] = useState({ lat: 20, lon: 0 });
  const drag = useRef(null);

  const paint = useCallback(() => {
    const cv = ref.current; if (!cv) return;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    const w = cv.clientWidth, h = cv.clientHeight;
    if (cv.width !== Math.round(w * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);

    const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2 - 12;
    /* THE UNWRAP. Both projections are computed and blended, so the sphere does not become a
       different component when it flattens — a candidate watches ONE Earth open out rather than
       being shown two pictures and asked to believe they are related. At morph 1 the far side is no
       longer hidden, because a chart has no far side: that IS the distortion arriving. */
    const P = (la, lo) => {
      const o = project(la, lo, rot.lat, rot.lon, R, cx, cy);
      if (morph <= 0) return o;
      const m = mercator(la, lo, rot.lon, Math.min(w / 2 - 8, R * 1.45), cx, cy);
      if (morph >= 1) return m;
      return o ? [o[0] + (m[0] - o[0]) * morph, o[1] + (m[1] - o[1]) * morph] : null;
    };

    // the globe itself
    if (morph < 1) {
      g.save(); g.globalAlpha = 1 - morph;
      g.beginPath(); g.arc(cx, cy, R, 0, Math.PI * 2);
      g.fillStyle = OCEAN; g.fill();
      g.strokeStyle = LIMB; g.lineWidth = 1.2; g.stroke();
      g.restore();
    }

    const path = (pts, style, width, dash = []) => {
      g.save(); g.setLineDash(dash); g.strokeStyle = style; g.lineWidth = width;
      g.beginPath(); let pen = false;
      for (const [la, lo] of pts) {
        const q = P(la, lo);
        if (!q) { pen = false; continue; }        // the far side is not drawn, it is ABSENT
        if (pen) g.lineTo(q[0], q[1]); else g.moveTo(q[0], q[1]);
        pen = true;
      }
      g.stroke(); g.restore();
    };
    const ring = (lat) => Array.from({ length: 145 }, (_, i) => [lat, -180 + i * 2.5]);
    const merid = (lon) => Array.from({ length: 73 }, (_, i) => [-90 + i * 2.5, lon]);

    if (overlays.graticule !== false) {
      for (let la = -60; la <= 60; la += 30) path(ring(la), GRID, 0.7);
      for (let lo = -150; lo < 180; lo += 30) path(merid(lo), GRID, 0.7);
    }
    if (overlays.parallels) for (const la of [66.5, 23.5, -23.5, -66.5]) path(ring(la), '#3f7fa0', 0.9, [3, 3]);
    // The two the chapter turns on, drawn heavier than the graticule so they read as special.
    if (overlays.equator !== false) path(ring(0), '#4fd08a', 2.0);
    if (overlays.primeMeridian !== false) path(merid(0), '#e0a83c', 2.0);

    if (route && route.from && route.to) {
      path(rhumb(route.from, route.to), '#e0642a', 1.8, [5, 4]);
      path(greatCircle(route.from, route.to), '#38b6e8', 2.0);
    }

    /* TISSOT. Circles of EQUAL true size on the Earth. On the globe they look equal; on the chart
       the high-latitude one is vast. That is the distortion measured rather than asserted, and it is
       the reason Greenland looks like Africa on a wall map. */
    if (overlays.tissot) {
      for (const la of [0, 30, 55, 70]) for (const lo of [-120, -40, 40, 120]) {
        const ring2 = [];
        for (let a = 0; a <= 360; a += 12) {
          const r = 6 * RAD, br = a * RAD, p1 = la * RAD;
          const p2 = Math.asin(Math.sin(p1) * Math.cos(r) + Math.cos(p1) * Math.sin(r) * Math.cos(br));
          const l2 = lo + Math.atan2(Math.sin(br) * Math.sin(r) * Math.cos(p1),
                                     Math.cos(r) - Math.sin(p1) * Math.sin(p2)) / RAD;
          ring2.push([p2 / RAD, l2]);
        }
        path(ring2, '#e0642a', 1.4);
      }
    }

    /* CIRCLES OF EQUAL ALTITUDE. A circle on the Earth at a given angular radius about a point —
       the same construction Tissot uses, generalised because a sight produces exactly this shape.
       Drawing it is the lesson: a single altitude does not give a position, it gives a CIRCLE, and
       the circle is thousands of miles across. */
    for (const c of (overlays.circles || [])) {
      const ring3 = [];
      const p1 = c.lat * RAD, r = c.radius * RAD;
      for (let a = 0; a <= 360; a += 3) {
        const br = a * RAD;
        const p2 = Math.asin(Math.sin(p1) * Math.cos(r) + Math.cos(p1) * Math.sin(r) * Math.cos(br));
        const l2 = c.lon + Math.atan2(Math.sin(br) * Math.sin(r) * Math.cos(p1),
                                      Math.cos(r) - Math.sin(p1) * Math.sin(p2)) / RAD;
        ring3.push([p2 / RAD, l2]);
      }
      path(ring3, c.colour || '#ffd479', c.width || 2, c.dash || []);
    }

    /* THE CELESTIAL FRAME. The Earth's axis extended outward, its poles projected onto a sphere
       drawn beyond the Earth, and the celestial equator as that sphere's own equator. Drawn OUTSIDE
       the globe so a candidate sees that it is a second, larger sphere concentric with this one —
       which is exactly what the celestial sphere is, and what a flat diagram cannot say. */
    if (overlays.celestial) {
      const CR = R * 1.34;
      g.save(); g.strokeStyle = '#8f7fd8'; g.lineWidth = 1; g.setLineDash([2, 4]);
      g.beginPath(); g.arc(cx, cy, CR, 0, Math.PI * 2); g.stroke(); g.restore();

      const CP = (la, lo) => {
        const q = project(la, lo, rot.lat, rot.lon, CR, cx, cy);
        return q;
      };
      // the celestial equator — the Earth's equator thrown outward
      const ceq = [];
      for (let k = 0; k <= 144; k++) ceq.push([0, -180 + k * 2.5]);
      g.save(); g.setLineDash([]); g.strokeStyle = '#8f7fd8'; g.lineWidth = 2;
      g.beginPath(); let pen2 = false;
      for (const [la, lo] of ceq) {
        const q = CP(la, lo);
        if (!q) { pen2 = false; continue; }
        if (pen2) g.lineTo(q[0], q[1]); else g.moveTo(q[0], q[1]);
        pen2 = true;
      }
      g.stroke(); g.restore();

      // the axis, and the celestial poles at its ends
      const np = project(90, 0, rot.lat, rot.lon, R, cx, cy);
      const cnp = CP(90, 0), csp = CP(-90, 0);
      g.save(); g.strokeStyle = '#8f7fd8'; g.lineWidth = 1.4; g.setLineDash([5, 4]);
      g.beginPath();
      const axN = project(90, 0, rot.lat, rot.lon, CR, cx, cy) || cnp;
      const axS = project(-90, 0, rot.lat, rot.lon, CR, cx, cy) || csp;
      if (axN) { g.moveTo(cx, cy); g.lineTo(axN[0], axN[1]); }
      if (axS) { g.moveTo(cx, cy); g.lineTo(axS[0], axS[1]); }
      g.stroke(); g.restore();
      for (const [pt, lbl] of [[cnp, 'celestial N'], [csp, 'celestial S']]) {
        if (!pt) continue;
        g.beginPath(); g.arc(pt[0], pt[1], 4, 0, Math.PI * 2);
        g.fillStyle = '#b9aef0'; g.fill();
        g.fillStyle = '#b9aef0'; g.font = '600 10px system-ui, sans-serif';
        g.fillText(lbl, pt[0] + 7, pt[1] - 5);
      }

      /* THE ZENITH — the point on the celestial sphere directly over the observer. Its declination
         IS his latitude, which is the identity this chapter turns on, and drawing it as a point
         above his head is what makes that arithmetic obvious. */
      for (const m of marks) {
        const z = CP(m.lat, m.lon);
        const o = project(m.lat, m.lon, rot.lat, rot.lon, R, cx, cy);
        if (z && o) {
          g.save(); g.strokeStyle = '#ffd479'; g.lineWidth = 1.6; g.setLineDash([3, 3]);
          g.beginPath(); g.moveTo(o[0], o[1]); g.lineTo(z[0], z[1]); g.stroke(); g.restore();
          g.beginPath(); g.arc(z[0], z[1], 4.5, 0, Math.PI * 2);
          g.fillStyle = '#ffd479'; g.fill();
          g.fillStyle = '#ffd479'; g.font = '600 10px system-ui, sans-serif';
          g.fillText('zenith', z[0] + 7, z[1] + 12);
        }
      }
    }

    /* CONVERGENCE — one degree of longitude, drawn at several latitudes.
       Bowditch: "at the equator a degree of longitude is equal to a degree of latitude, but
       approaching the poles… the degrees of longitude become less and less." That sentence was in
       the reading and NOT on the sphere, which the founder spotted. Equal arcs of longitude drawn at
       rising latitude are visibly shorter, because the meridians are closing on each other — and it
       is the same fact as departure = dLong × cos(lat), seen rather than stated. */
    if (overlays.convergence) {
      for (const la of [0, 30, 60, 75]) {
        const seg = [];
        for (let k = 0; k <= 24; k++) seg.push([la, -60 + (k / 24) * 15]);   // 15° of longitude
        path(seg, la === 0 ? '#4fd08a' : '#38b6e8', 3);
        const cap = P(la, -60);
        if (cap) {
          g.fillStyle = '#cfe4f2'; g.font = '600 10px system-ui, sans-serif';
          // 15° of longitude = 900' of dLong; its true length is 900·cos(lat) nautical miles.
          g.fillText(`${(900 * Math.cos(la * RAD)).toFixed(0)} nm`, cap[0] - 46, cap[1] + 3);
        }
      }
    }

    /* POLES — the two norths, drawn as places rather than described as a difference. A candidate who
       has seen them sit apart on the Earth has the cause of variation before he meets the word. */
    if (overlays.poles) {
      const tp = P(90, 0);
      if (tp) {
        g.beginPath(); g.arc(tp[0], tp[1], 4.5, 0, Math.PI * 2);
        g.fillStyle = '#eaf3f8'; g.fill();
        g.fillStyle = '#eaf3f8'; g.font = '600 11px system-ui, sans-serif';
        g.fillText('true N', tp[0] + 8, tp[1] - 6);
      }
      const mp = P(NMP.lat, NMP.lon);
      if (mp) {
        g.beginPath(); g.arc(mp[0], mp[1], 4.5, 0, Math.PI * 2);
        g.fillStyle = '#e0642a'; g.fill();
        g.fillStyle = '#e0642a'; g.font = '600 11px system-ui, sans-serif';
        g.fillText('magnetic N', mp[0] + 8, mp[1] + 12);
      }
      // The two meridians the vessel actually sees: toward each pole, from wherever she is.
      for (const m of marks) {
        path(greatCircle([m.lat, m.lon], [90, 0], 48), '#eaf3f8', 1.6, [4, 3]);
        path(greatCircle([m.lat, m.lon], [NMP.lat, NMP.lon], 48), '#e0642a', 1.8);
      }
    }

    for (const m of marks) {
      const q = P(m.lat, m.lon); if (!q) continue;
      // THE ARCS ARE THE LESSON. Latitude is the meridional arc from the Equator; longitude is the
      // equatorial arc from Greenwich. Drawn as arcs, because the sentence describing them is what
      // a cadet can read three times without seeing.
      if (m.arcs !== false) {
        const nLat = Math.max(2, Math.round(Math.abs(m.lat) / 2));
        path(Array.from({ length: nLat + 1 }, (_, i) => [m.lat * (i / nLat), m.lon]), '#4fd08a', 2.4);
        const nLon = Math.max(2, Math.round(Math.abs(m.lon) / 2));
        path(Array.from({ length: nLon + 1 }, (_, i) => [0, m.lon * (i / nLon)]), '#e0a83c', 2.4);
      }
      g.beginPath(); g.arc(q[0], q[1], 5.5, 0, Math.PI * 2);
      g.fillStyle = m.colour || '#ffffff'; g.fill();
      g.strokeStyle = '#0b1c28'; g.lineWidth = 1.5; g.stroke();
      if (m.label) {
        g.fillStyle = '#eaf3f8'; g.font = '600 12px system-ui, sans-serif';
        g.fillText(m.label, q[0] + 9, q[1] - 8);
      }
    }
  }, [rot, overlays, marks, route]);

  useEffect(() => { paint(); }, [paint]);
  useEffect(() => {
    const ro = new ResizeObserver(paint); if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [paint]);

  const at = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top, r];
  };

  return (
    <canvas ref={ref} className="nav-sphere" style={{ height }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        const [x, y, r] = at(e);
        const R = Math.min(r.width, r.height) / 2 - 12;
        const hit = onMove && unproject(x, y, rot.lat, rot.lon, R, r.width / 2, r.height / 2);
        // Dragging INSIDE the disc moves the vessel; dragging anywhere spins the Earth. A cadet who
        // grabs the ship expects the ship to move, not the world.
        drag.current = hit && marks.some((m) => {
          const q = project(m.lat, m.lon, rot.lat, rot.lon, R, r.width / 2, r.height / 2);
          return q && Math.hypot(q[0] - x, q[1] - y) < 22;
        }) ? { kind: 'mark' } : { kind: 'spin', x, y, lat: rot.lat, lon: rot.lon };
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const [x, y, r] = at(e);
        if (drag.current.kind === 'spin') {
          setRot({ lat: clamp(drag.current.lat + (y - drag.current.y) * 0.4, -85, 85),
                   lon: drag.current.lon - (x - drag.current.x) * 0.4 });
        } else if (onMove) {
          const R = Math.min(r.width, r.height) / 2 - 12;
          const p = unproject(x, y, rot.lat, rot.lon, R, r.width / 2, r.height / 2);
          if (p) onMove(p[0], p[1]);
        }
      }}
      onPointerUp={() => { drag.current = null; }}
      onPointerCancel={() => { drag.current = null; }} />
  );
}

/* ── THE OVERLAYS THIS API IS MEANT TO CARRY ──────────────────────────────────────────────────
   Listed so the shape is judged against its intended callers now, rather than found wanting later.
   Each is an overlay or a mark on THIS object, never a second globe:

     ch1  latitude, longitude, Equator, Greenwich, N/S/E/W        equator, primeMeridian, arcs
     ch2  Mercator — unwrap the sphere, show WHY it distorts      (a projection prop: 'ortho'|'mercator')
     ch3  compass — true north, magnetic north, variation         marks + a bearing overlay
     ch4  celestial frame — axis extended, celestial poles        overlays.axis, overlays.celestial
     ch5  sailings — great circle vs rhumb, DLat, DLong           route  (already implemented)
     ch6  sights — GP of the body, altitude circle, intercept     marks + circle overlay
*/
